import {
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
  Language as LanguageIcon,
  Phone as PhoneIcon,
  PictureAsPdf,
  Print as PrintIcon,
  Save as SaveIcon,
  UploadFile,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import type { AlertColor } from "@mui/material/Alert";
import { useQueryClient as useTanStackQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FieldErrors } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import type QueryClient from "../../../api/query/queryClient";
import {
  calculateDaysUntilAppointment,
  formatDate,
  formatDateTime,
  formatDaysUntilAppointment,
} from "../../../utils/dateUtils";
import { isoNow, preserveUrlParams } from "../../../utils/utils";
import { formatVerificationOutcome } from "../../../utils/verification";
import {
  PostedState,
  postInsuranceVerificationPMSUpdateAndNotify,
} from "../../posting";
import type { InsuranceVerificationFormFields } from "../form/validation.zod";
import { IV_LIST_KEY } from "../hooks";
import { useInsuranceVerificationForm } from "../hooks/useInsuranceVerificationForm";
import type {
  InsuranceVerificationStore,
  InsuranceVerificationStoreAction,
} from "../store";
import PostButton from "../table/PostButton";
import type { AppliedRuleDisplay } from "../utils/agentRules";
import AppliedRulesDialog from "./AppliedRulesDialog";
import CarrierContact from "./CarrierContact";
import CompletionModal from "./CompletionModal";
import ContactInformationCard from "./ContactInformationCard";
import EvalCaseActions from "./EvalCaseActions";
import PDFSidebar from "./PDFSidebar";
import SkipActions from "./SkipActions";

interface InsuranceVerificationDetailPageProps {
  store: InsuranceVerificationStore;
  updateStore: (action: InsuranceVerificationStoreAction) => void;
  queryClient: QueryClient;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  supportedPayers: CredentialsSupportedPayersMessage[];
}

function getVerifyErrorMessage(error: unknown): string {
  const fallback = "Failed to complete verification";
  if (!isAxiosError(error)) return fallback;
  const status = error.response?.status;
  if (!status || status >= 500) return fallback;
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return `${fallback}: ${detail}`;
  if (Array.isArray(detail) && typeof detail[0]?.msg === "string") {
    return `${fallback}: ${detail[0].msg.replace(/^Value error, /, "")}`;
  }
  return fallback;
}

function shouldAutoExpandPDF(hasPdfAttached?: boolean): boolean {
  return hasPdfAttached === true;
}

function getVerificationMethodDisplay(method: string | null): {
  label: string;
  icon: React.ReactNode;
} | null {
  if (!method) return null;

  switch (method) {
    case "PHONE_CALL":
      return { label: "Phone Call", icon: <PhoneIcon fontSize="small" /> };
    case "PAYER_PORTAL":
      return { label: "Payer Portal", icon: <LanguageIcon fontSize="small" /> };
    case "FAX":
      return { label: "Fax", icon: <PrintIcon fontSize="small" /> };
    default:
      return { label: method, icon: null };
  }
}

function hasFormData(
  verification:
    | InsuranceVerificationMessage
    | InsuranceVerificationUpdateMessage,
): boolean {
  return !!(
    verification.hasOrthodonticCoverage ||
    verification.effectiveDate ||
    verification.terminationDate
  );
}

const SKIP_ACTION_OUTCOMES: SkipActionType[] = [
  "ALREADY_VERIFIED",
  "NOT_REQUIRED",
  "SECONDARY_INSURANCE",
  "INSUFFICIENT_DATA",
];

const CARRIER_AVAILABLE_OUTCOMES = [
  "COVERAGE_ACTIVE_HAS_ORTHO",
  "COVERAGE_ACTIVE_NO_ORTHO",
  "COVERAGE_ACTIVE",
  "COVERAGE_INACTIVE",
];

interface InitialSelection {
  skipAction: SkipActionType | null;
  carrierContact: CarrierContact | null;
}

// Derived purely from the verification's outcome so the form is pre-filled
// consistently for every status/outcome pairing (Benny's automated REVIEW
// findings included), not just the statuses a human's own flow produces.
function getInitialSelection(
  detail: InsuranceVerificationMessage,
): InitialSelection {
  const outcome = detail.verificationOutcome;

  if (outcome && SKIP_ACTION_OUTCOMES.includes(outcome as SkipActionType)) {
    return { skipAction: outcome as SkipActionType, carrierContact: null };
  }
  if (outcome === "CARRIER_UNAVAILABLE" || outcome === "PATIENT_NOT_FOUND") {
    return { skipAction: null, carrierContact: outcome };
  }
  if (outcome && CARRIER_AVAILABLE_OUTCOMES.includes(outcome)) {
    return { skipAction: null, carrierContact: "CARRIER_AVAILABLE" };
  }
  // No outcome recorded yet — if Benny already filled in coverage details,
  // reflect that so the reviewer sees the data instead of a blank form.
  if (hasFormData(detail)) {
    return { skipAction: null, carrierContact: "CARRIER_AVAILABLE" };
  }
  return { skipAction: null, carrierContact: null };
}

export default function InsuranceVerificationDetailPage({
  store,
  updateStore,
  queryClient,
  updateSnackBar,
  supportedPayers,
}: InsuranceVerificationDetailPageProps) {
  const { verificationId } = useParams<{ verificationId: string }>();
  const navigate = useNavigate();
  const rqClient = useTanStackQueryClient();
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("COMPLETED");
  const [selectedOutcome, setSelectedOutcome] = useState<string>("");

  const [showPDFSidebar, setShowPDFSidebar] = useState(
    shouldAutoExpandPDF(!!store.currentDetail?.bennyRetrievedFileId),
  );
  const [appliedRules, setAppliedRules] = useState<AppliedRuleDisplay[]>([]);
  const [showRulesDialog, setShowRulesDialog] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState<string | null>(
    null,
  );
  const [callReferenceNumber, setCallReferenceNumber] = useState<string | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initialFormDataSnapshot = useRef<string | null>(null);
  const payerMappedPollIntervalRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  const productTypeUpdatedPollIntervalRef = useRef<ReturnType<
    typeof setInterval
  > | null>(null);

  const formHook = useInsuranceVerificationForm({
    verification: store.currentDetail || null,
    onUpdate: async (updatePayload: InsuranceVerificationUpdateMessage) => {
      await queryClient.updatePatientInsuranceVerification(updatePayload);
      updateStore({
        type: "VERIFICATION_UPDATED",
        verificationUpdate: updatePayload,
      });
      rqClient.invalidateQueries({ queryKey: IV_LIST_KEY });
    },
    onVerify: async (request: VerifyInsuranceRequest) => {
      await queryClient.verifyInsurance(request);
      updateStore({
        type: "VERIFICATION_UPDATED",
        verificationUpdate: {
          wieldyId: request.wieldyId,
          practiceId: store.currentDetail?.practiceId ?? "",
          verificationStatus: request.status,
          verificationOutcome: request.outcome,
        },
      });
      rqClient.invalidateQueries({ queryKey: IV_LIST_KEY });
    },
    onValidationChange: (_errors, _warnings) => undefined,
  });

  const handleFormPredictedOutcomeChange = useCallback(
    (outcome: string) => {
      if (store.currentDetail) {
        formHook.handlePredictedOutcomeChange(outcome);
      }
    },
    [formHook, store.currentDetail],
  );

  const handleFormDataChange = useCallback(
    (data: InsuranceVerificationDetailUpdate) => {
      if (store.currentDetail) {
        formHook.handleFormDataChange(data);
      }
    },
    [formHook, store.currentDetail],
  );

  const handleFormValidationChange = useCallback(
    (
      errors: FieldErrors<InsuranceVerificationFormFields>,
      warnings: string[],
    ) => {
      if (store.currentDetail) {
        formHook.handleValidationChange(errors, warnings);
      }
    },
    [formHook, store.currentDetail],
  );
  const hasLoadedRef = useRef(false);

  // Centralized selection state to coordinate between components
  const [selectedSkipAction, setSelectedSkipAction] =
    useState<SkipActionType | null>(null);
  const [selectedCarrierContact, setSelectedCarrierContact] =
    useState<CarrierContact | null>(null);

  const hasPayer = Boolean(store.currentDetail?.payerId);
  const FINAL_STATUSES = [
    "COMPLETED",
    "SKIPPED",
    "MISSED",
    "REVERIFY",
    "DUPLICATE",
  ];
  const isInFinalState = FINAL_STATUSES.includes(
    store.currentDetail?.verificationStatus ?? "",
  );
  const isDuplicate = store.currentDetail?.verificationStatus === "DUPLICATE";
  const showPayerGate = !hasPayer && !isInFinalState;

  const getCurrentAction = () => {
    if (selectedSkipAction) return "skip";
    if (
      selectedCarrierContact &&
      selectedCarrierContact !== "CARRIER_AVAILABLE"
    )
      return "carrier";
    if (selectedCarrierContact === "CARRIER_AVAILABLE") return "form";
    return null;
  };

  const getOutcomeAndStatus = () => {
    if (selectedSkipAction) {
      return { status: "SKIPPED", outcome: selectedSkipAction };
    }
    if (selectedCarrierContact === "CARRIER_UNAVAILABLE") {
      return { status: "COMPLETED", outcome: "CARRIER_UNAVAILABLE" };
    }
    if (selectedCarrierContact === "PATIENT_NOT_FOUND") {
      return { status: "COMPLETED", outcome: "PATIENT_NOT_FOUND" };
    }
    if (selectedCarrierContact === "CARRIER_AVAILABLE") {
      const outcome = formHook.predictedOutcome || "COVERAGE_ACTIVE_HAS_ORTHO";
      return { status: "COMPLETED", outcome };
    }
    return null;
  };

  const hasChanges = () => {
    const action = getCurrentAction();
    if (!action) return false;
    if (action === "form") {
      // For REVIEW verifications with Benny-filled data, allow completion without
      // requiring the user to touch any field — they're just confirming existing data.
      if (
        store.currentDetail?.verificationStatus === "REVIEW" &&
        hasFormData(store.currentDetail)
      ) {
        return true;
      }
      if (formHook.formData === null) return false;
      const serialized = JSON.stringify(formHook.formData);
      if (initialFormDataSnapshot.current === null) {
        // First access after load — capture the form's initial (mount) values as baseline
        initialFormDataSnapshot.current = serialized;
        return false;
      }
      return serialized !== initialFormDataSnapshot.current;
    }
    if (action === "skip") {
      if (store.currentDetail?.verificationStatus === "SKIPPED") {
        return selectedSkipAction !== store.currentDetail.verificationOutcome;
      }
      return true;
    }
    if (action === "carrier") {
      if (store.currentDetail?.verificationStatus === "COMPLETED") {
        if (store.currentDetail.verificationOutcome === "CARRIER_UNAVAILABLE") {
          return selectedCarrierContact !== "CARRIER_UNAVAILABLE";
        }
        if (store.currentDetail.verificationOutcome === "PATIENT_NOT_FOUND") {
          return selectedCarrierContact !== "PATIENT_NOT_FOUND";
        }
      }
      return true;
    }
    return true;
  };

  const isCompleteButtonDisabled = () => !hasChanges() || formHook.isSubmitting;

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding navigate, updateSnackBar, store.currentDetail and 1 more would change when this runs
  const loadDetailData = useCallback(
    async (silent = false) => {
      if (!verificationId) {
        updateSnackBar({
          severity: "error",
          message: "Invalid verification ID",
        });
        navigate(preserveUrlParams("/insurance-verification"));
        return undefined;
      }

      // Prevent loading if already loading
      if (store.detailLoading) {
        return undefined;
      }

      try {
        if (!silent) {
          updateStore({ type: "DETAIL_LOADING" });
        }

        const detail =
          await queryClient.getInsuranceVerificationDetail(verificationId);

        // Set loaded flag only after successful load
        hasLoadedRef.current = true;

        // Only update the store (and trigger a re-render) when the fetched data
        // differs from what is already in the store. This prevents unnecessary
        // re-renders when loadDetailData is called as part of the payer-mapping
        // poll and the verification has not changed.
        const hasChanged =
          JSON.stringify(detail) !== JSON.stringify(store.currentDetail);
        if (!hasChanged) {
          return detail;
        }

        updateStore({ type: "DETAIL_LOADED", detail });
        initialFormDataSnapshot.current = null;

        // Pre-select/fill the form to match the verification's current
        // outcome, regardless of status — covers Benny's automated REVIEW
        // findings (e.g. INSUFFICIENT_DATA, PATIENT_NOT_FOUND) as well as
        // the human-driven SKIPPED/COMPLETED/REVERIFY flows.
        const { skipAction, carrierContact } = getInitialSelection(detail);
        setSelectedSkipAction(skipAction);
        setSelectedCarrierContact(carrierContact);
        setShowPDFSidebar(shouldAutoExpandPDF(!!detail?.bennyRetrievedFileId));
        return detail;
      } catch (_error) {
        // Reset loaded flag on error to allow retry
        hasLoadedRef.current = false;
        updateStore({
          type: "DETAIL_ERROR",
          error: "Failed to load verification details",
        });
        updateSnackBar({
          severity: "error",
          message: "Failed to load verification details",
        });
        return undefined;
      }
    },
    [verificationId, store.detailLoading, queryClient],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding loadDetailData, updateStore would change when this runs
  useEffect(() => {
    const loadDetails = async () => {
      if (!verificationId) return;
      if (hasLoadedRef.current) return;
      await loadDetailData();
    };

    loadDetails();

    return () => {
      hasLoadedRef.current = false;
      setSelectedCarrierContact(null);
      setSelectedSkipAction(null);
      updateStore({ type: "DETAIL_RESET" });
      if (payerMappedPollIntervalRef.current !== null) {
        clearInterval(payerMappedPollIntervalRef.current);
        payerMappedPollIntervalRef.current = null;
      }
    };
  }, [verificationId]);

  useEffect(() => {
    setShowPDFSidebar(
      shouldAutoExpandPDF(!!store.currentDetail?.bennyRetrievedFileId),
    );
  }, [store.currentDetail?.bennyRetrievedFileId]);

  // Coordinated handlers that reset the other selection
  const handleSkipActionChange = useCallback(
    (skipAction: SkipActionType | null) => {
      setSelectedSkipAction(skipAction);
      setSelectedCarrierContact(null); // Reset carrier contact when skip is selected
    },
    [],
  );

  const handleCarrierContactChange = useCallback(
    (carrierContact: CarrierContact) => {
      setSelectedCarrierContact(carrierContact);
      setSelectedSkipAction(null); // Reset skip action when carrier contact is selected
    },
    [],
  );

  const handleCompleteVerification = useCallback(() => {
    const statusAndOutcome = getOutcomeAndStatus();
    if (statusAndOutcome) {
      setSelectedStatus(statusAndOutcome.status);
      setSelectedOutcome(statusAndOutcome.outcome);
    }
    setVerificationMethod(
      (prev) => prev ?? store.currentDetail?.verificationMethod ?? null,
    );
    setCallReferenceNumber(
      (prev) =>
        prev ??
        formHook.formData?.callReferenceNumber ??
        store.currentDetail?.callReferenceNumber ??
        null,
    );
    setShowCompletionModal(true);
  }, [
    // biome-ignore lint/correctness/useExhaustiveDependencies: getOutcomeAndStatus is recreated every render; fixing changes when this runs
    getOutcomeAndStatus,
    store.currentDetail?.verificationMethod,
    store.currentDetail?.callReferenceNumber,
    formHook.formData?.callReferenceNumber,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding verificationMethod would change when this runs
  const handleConfirmCompletion = useCallback(async () => {
    const currentAction = getCurrentAction();
    setShowCompletionModal(false);

    if (!verificationId || !store.currentDetail) return;

    try {
      if (currentAction === "skip" && selectedSkipAction) {
        await formHook.submitSkipAction(selectedSkipAction);
      } else if (currentAction === "carrier" && selectedCarrierContact) {
        await formHook.submitCarrierContactOutcome(selectedCarrierContact);
      } else if (currentAction === "form") {
        await formHook.submitVerify({
          explicitStatus: selectedStatus as VerificationStatus,
          explicitOutcome: selectedOutcome as VerificationOutcome,
          verificationMethod,
          callReferenceNumber,
        });
      }

      updateSnackBar({
        severity: "success",
        message: `Verification completed successfully for ${store.currentDetail?.patientName}`,
      });

      // Invalidate the list and stay on the detail page so the Post button is
      // immediately available after completing a REVIEW verification.
      rqClient.invalidateQueries({ queryKey: IV_LIST_KEY });
      initialFormDataSnapshot.current = null;
      await loadDetailData(true);
    } catch (error) {
      updateSnackBar({
        severity: "error",
        message: getVerifyErrorMessage(error),
      });
    }
  }, [
    // biome-ignore lint/correctness/useExhaustiveDependencies: getCurrentAction is recreated every render; fixing changes when this runs
    getCurrentAction,
    selectedSkipAction,
    selectedCarrierContact,
    selectedStatus,
    selectedOutcome,
    formHook,
    verificationId,
    store.currentDetail,
    updateStore,
    updateSnackBar,
    loadDetailData,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding FINAL_STATUSES.includes would change when this runs
  const handlePayerMapped = useCallback(
    async (payerId: string, payerName: string) => {
      if (!store.currentDetail) return;
      try {
        await queryClient.mapInsuranceVerificationPayer(
          store.currentDetail.wieldyId,
          payerId,
        );
        updateSnackBar({
          severity: "success",
          message: "Insurance company mapped successfully",
        });
        // Optimistically unlock the form — the CRUD events are async (SQS in
        // production), so patch payerId and payerName in the store immediately.
        updateStore({
          type: "DETAIL_LOADED",
          detail: { ...store.currentDetail, payerId, payerName },
        });

        // Poll for status changes (e.g. DUPLICATE) set asynchronously via SQS.
        const POLL_INTERVAL_MS = 3000;
        const MAX_ATTEMPTS = 10;
        let attempts = 0;
        payerMappedPollIntervalRef.current = setInterval(async () => {
          attempts += 1;
          const detail = await loadDetailData(true);
          if (
            FINAL_STATUSES.includes(detail?.verificationStatus ?? "") ||
            attempts >= MAX_ATTEMPTS
          ) {
            if (payerMappedPollIntervalRef.current !== null) {
              clearInterval(payerMappedPollIntervalRef.current);
              payerMappedPollIntervalRef.current = null;
            }
          }
        }, POLL_INTERVAL_MS);
      } catch (_error) {
        updateSnackBar({
          severity: "error",
          message: "Failed to map insurance company",
        });
      }
    },
    [
      store.currentDetail,
      queryClient,
      updateSnackBar,
      updateStore,
      loadDetailData,
    ],
  );

  const handleProductTypeUpdated = useCallback(
    async (newProductType: string) => {
      if (!store.currentDetail) return;
      if (!store.currentDetail.insuranceKey) return;
      const { insuranceKey } = store.currentDetail;
      const originalNetworkStatus =
        store.currentDetail.locationInfo?.networkStatus;
      try {
        await queryClient.updateInsurance({
          wieldyId: insuranceKey,
          productType: newProductType,
        });
        updateSnackBar({
          severity: "success",
          message: "Product type updated",
        });
        updateStore({
          type: "DETAIL_LOADED",
          detail: { ...store.currentDetail, wieldyProductType: newProductType },
        });

        const POLL_INTERVAL_MS = 3000;
        const MAX_ATTEMPTS = 10;
        let attempts = 0;
        productTypeUpdatedPollIntervalRef.current = setInterval(async () => {
          attempts += 1;
          const detail = await loadDetailData(true);
          if (
            // biome-ignore lint/complexity/useOptionalChain: a missing detail must not read as a status change
            (detail &&
              detail.locationInfo &&
              detail.locationInfo.networkStatus !== originalNetworkStatus) ||
            attempts >= MAX_ATTEMPTS
          ) {
            if (productTypeUpdatedPollIntervalRef.current !== null) {
              clearInterval(productTypeUpdatedPollIntervalRef.current);
              productTypeUpdatedPollIntervalRef.current = null;
            }
          }
        }, POLL_INTERVAL_MS);
      } catch (_error) {
        updateSnackBar({
          severity: "error",
          message: "Failed to update product type",
        });
      }
    },
    [
      store.currentDetail,
      queryClient,
      updateSnackBar,
      updateStore,
      loadDetailData,
    ],
  );

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handlePDFToggle = useCallback(() => {
    // If no PDF exists and sidebar is hidden, trigger file upload directly
    if (!store.currentDetail?.bennyRetrievedFileId && !showPDFSidebar) {
      fileInputRef.current?.click();
      return;
    }
    setShowPDFSidebar(!showPDFSidebar);
  }, [showPDFSidebar, store.currentDetail?.bennyRetrievedFileId]);

  const getPDFButtonText = () => {
    if (showPDFSidebar) return "Hide PDF";
    if (store.currentDetail?.bennyRetrievedFileId) return "Show PDF";
    return "Upload PDF";
  };

  const handleUploadPdf = useCallback(
    async (file: File) => {
      if (!verificationId) {
        throw new Error("Verification ID not found");
      }

      if (!store.currentDetail?.practiceId) {
        throw new Error("Practice ID not found");
      }

      try {
        const fileResponse = await queryClient.saveFile(
          store.currentDetail.practiceId,
          "WIELDY",
          file,
        );

        const payload = {
          wieldyId: verificationId,
          practiceId: store.currentDetail.practiceId,
        };
        await queryClient.updatePatientInsuranceVerification({
          ...payload,
          fileId: fileResponse.id,
        });
        updateStore({
          type: "DETAIL_LOADED",
          detail: {
            ...store.currentDetail,
            bennyRetrievedFileId: fileResponse.id,
            bennyRetrievedFilePath: fileResponse.saved_location,
            bennyRetrievalStatus: null,
          },
        });

        updateSnackBar({
          severity: "success",
          message: "PDF uploaded successfully",
        });
      } catch (error) {
        updateSnackBar({
          severity: "error",
          message:
            error instanceof Error ? error.message : "Failed to upload PDF",
        });
        throw error;
      }
    },
    [
      verificationId,
      store.currentDetail,
      queryClient,
      updateStore,
      updateSnackBar,
    ],
  );

  const handleRemovePdf = useCallback(async () => {
    if (!verificationId) {
      throw new Error("Verification ID not found");
    }

    if (!store.currentDetail?.practiceId) {
      throw new Error("Practice ID not found");
    }

    try {
      await queryClient.updatePatientInsuranceVerification({
        wieldyId: verificationId,
        practiceId: store.currentDetail.practiceId,
        fileId: null,
      });
      updateStore({
        type: "DETAIL_LOADED",
        detail: {
          ...store.currentDetail,
          bennyRetrievedFileId: null,
          bennyRetrievedFilePath: null,
        },
      });

      updateSnackBar({
        severity: "success",
        message: "PDF removed successfully",
      });
    } catch (error) {
      updateSnackBar({
        severity: "error",
        message:
          error instanceof Error ? error.message : "Failed to remove PDF",
      });
      throw error;
    }
  }, [
    verificationId,
    store.currentDetail,
    queryClient,
    updateStore,
    updateSnackBar,
  ]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (file.type !== "application/pdf") {
        updateSnackBar({
          severity: "error",
          message: "Please select a PDF file",
        });
        return;
      }

      try {
        await handleUploadPdf(file);
        setShowPDFSidebar(true);
      } catch {
        // Error is already handled in handleUploadPdf
      }

      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [handleUploadPdf, updateSnackBar],
  );

  if (store.detailLoading || (!store.currentDetail && !store.detailError)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (store.detailError) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {store.detailError}
        </Alert>
        <Button variant="outlined" onClick={handleBack}>
          Back to Verifications
        </Button>
      </Box>
    );
  }

  if (!store.currentDetail) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Verification not found
        </Alert>
        <Button variant="outlined" onClick={handleBack}>
          Back to Verifications
        </Button>
      </Box>
    );
  }

  const { currentDetail } = store;

  const renderFormSection = () => {
    if (isDuplicate) {
      return (
        <Box
          data-testid="duplicateVerificationBanner"
          sx={{
            p: 3,
            m: 3,
            backgroundColor: "#e3f2fd",
            borderRadius: 2,
            border: "1px solid #90caf9",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "#1565c0", mb: 0.5 }}
          >
            Duplicate Verification
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            This verification has been identified as a duplicate. No further
            action is required.
          </Typography>
        </Box>
      );
    }
    if (!showPayerGate) {
      return (
        <>
          <Box sx={{ p: 3 }}>
            <SkipActions
              onSkipActionChange={handleSkipActionChange}
              selectedSkipAction={selectedSkipAction}
              verification={currentDetail}
            />
          </Box>

          <Divider />

          <Box sx={{ p: 3 }}>
            <CarrierContact
              verification={currentDetail}
              onCarrierContactChange={handleCarrierContactChange}
              selectedCarrierContact={selectedCarrierContact}
              onPredictedOutcomeChange={handleFormPredictedOutcomeChange}
              onFormDataChange={handleFormDataChange}
              onValidationChange={handleFormValidationChange}
              onAppliedRulesChange={setAppliedRules}
            />
          </Box>
        </>
      );
    }
    return (
      <Box
        data-testid="payerGateWarning"
        sx={{
          p: 3,
          m: 3,
          backgroundColor: "#fff8e1",
          borderRadius: 2,
          border: "1px solid #ffcc02",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#ef6c00", mb: 0.5 }}
        >
          Action required: Tag a Wieldy Payer Name
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          This verification cannot be completed until the insurance company is
          tagged with a Wieldy Payer Name. Use the Company field above to tag
          it.
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        ...(showPDFSidebar && {
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          zIndex: 1300,
          backgroundColor: "background.default",
        }),
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 3, pb: 2, flexShrink: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                variant="outlined"
                sx={{ minWidth: "auto" }}
              >
                Back
              </Button>
              <Typography variant="h4" sx={{ fontWeight: 600 }} data-sr-redact>
                {store.currentDetail.patientName}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Verification Method Badge - shown for completed verifications */}
              {store.currentDetail.verificationStatus === "COMPLETED" &&
                store.currentDetail.verificationMethod && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      backgroundColor: "#e8f5e8",
                      borderRadius: "8px",
                      border: "1px solid #c8e6c9",
                    }}
                  >
                    <Box
                      sx={{
                        color: "#2e7d32",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {
                        getVerificationMethodDisplay(
                          store.currentDetail.verificationMethod,
                        )?.icon
                      }
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#2e7d32",
                        fontWeight: 500,
                        fontSize: "0.875rem",
                      }}
                    >
                      Verified
                      {store.currentDetail.verifiedAt &&
                        ` on ${formatDate(store.currentDetail.verifiedAt)}`}{" "}
                      via{" "}
                      {
                        getVerificationMethodDisplay(
                          store.currentDetail.verificationMethod,
                        )?.label
                      }
                    </Typography>
                  </Box>
                )}

              {appliedRules.length > 0 &&
                (() => {
                  const overriddenCount = appliedRules.filter(
                    (r) => r.status === "overridden",
                  ).length;
                  const hasOverrides = overriddenCount > 0;
                  return (
                    <Box
                      onClick={() => setShowRulesDialog(true)}
                      data-testid="bennyRulesChip"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        backgroundColor: hasOverrides ? "#fff8e1" : "grey.100",
                        border: "1px solid",
                        borderColor: hasOverrides ? "#ffcc02" : "grey.300",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: hasOverrides
                            ? "#ffecb3"
                            : "grey.200",
                        },
                      }}
                    >
                      <AutoAwesomeIcon
                        sx={{
                          fontSize: 16,
                          color: hasOverrides ? "#ef6c00" : "#1976d2",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: "0.875rem",
                          color: hasOverrides ? "#ef6c00" : "text.primary",
                        }}
                      >
                        {hasOverrides
                          ? `Benny rules (${appliedRules.length}) · ${overriddenCount} overridden`
                          : `Benny rules (${appliedRules.length})`}
                      </Typography>
                    </Box>
                  );
                })()}

              <Box
                onClick={handlePDFToggle}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: showPDFSidebar ? "primary.main" : "grey.100",
                  color: showPDFSidebar ? "white" : "text.primary",
                  border: "1px solid",
                  borderColor: showPDFSidebar ? "primary.main" : "grey.300",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: showPDFSidebar
                      ? "primary.dark"
                      : "grey.200",
                  },
                }}
              >
                {store.currentDetail?.bennyRetrievedFileId ? (
                  <PictureAsPdf sx={{ fontSize: 16 }} />
                ) : (
                  <UploadFile sx={{ fontSize: 16 }} />
                )}
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, fontSize: "0.875rem" }}
                >
                  {getPDFButtonText()}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <Box component="thead">
              <Box component="tr">
                <Box
                  component="td"
                  sx={{
                    borderLeft: "0px",
                    paddingLeft: "0px",
                    padding: "0 16px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 500,
                      lineHeight: "20px",
                      letterSpacing: "1px",
                    }}
                  >
                    <Box component="span" sx={{ color: "#475467" }}>
                      Appointment:{" "}
                    </Box>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {formatDateTime(store.currentDetail.appointmentDatetime)}{" "}
                      (
                      {formatDaysUntilAppointment(
                        calculateDaysUntilAppointment(
                          store.currentDetail.appointmentDatetime,
                        ),
                      )}
                      )
                    </Box>
                  </Typography>
                </Box>

                <Box
                  component="td"
                  sx={{
                    borderLeft: "1px solid #d0d5dd",
                    padding: "0 16px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 500,
                      lineHeight: "20px",
                      letterSpacing: "1px",
                    }}
                  >
                    <Box component="span" sx={{ color: "#475467" }}>
                      Status:{" "}
                    </Box>
                    <Box
                      component="span"
                      data-testid="verificationStatus"
                      sx={{ fontWeight: 600 }}
                    >
                      {store.currentDetail.verificationStatus || "QUEUED"}
                    </Box>
                  </Typography>
                </Box>

                <Box
                  component="td"
                  sx={{
                    borderLeft: "1px solid #d0d5dd",
                    padding: "0 16px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 500,
                      lineHeight: "20px",
                      letterSpacing: "1px",
                    }}
                  >
                    <Box component="span" sx={{ color: "#475467" }}>
                      Outcome:{" "}
                    </Box>
                    <Box
                      component="span"
                      data-testid="verificationOutcome"
                      sx={{ fontWeight: 600 }}
                    >
                      {store.currentDetail.verificationOutcome
                        ? formatVerificationOutcome(
                            store.currentDetail.verificationOutcome,
                          )
                        : "Pending"}
                    </Box>
                  </Typography>
                </Box>

                <Box
                  component="td"
                  sx={{
                    borderLeft: "1px solid #d0d5dd",
                    padding: "0 16px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 500,
                      lineHeight: "20px",
                      letterSpacing: "1px",
                    }}
                  >
                    <Box component="span" sx={{ color: "#475467" }}>
                      Appointment Retrieved:{" "}
                    </Box>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {store.currentDetail.firstRetrieved
                        ? new Date(
                            store.currentDetail.firstRetrieved,
                          ).toLocaleDateString(undefined, { timeZone: "UTC" })
                        : "-"}
                    </Box>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              px: 3,
              flex: 1,
              position: "relative",
            }}
          >
            <Box
              sx={{
                flex: showPDFSidebar ? "0 0 55%" : "1 1 100%",
                transition: "flex 0.2s ease",
                maxWidth: showPDFSidebar ? "55%" : "100%",
                minWidth: showPDFSidebar ? "450px" : "auto",
                overflow: "auto",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "background.paper",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 1,
                  overflow: "hidden",
                }}
              >
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        backgroundColor: "primary.main",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "white", fontSize: "12px" }}
                      >
                        📞
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Contact Information for Verification Call
                    </Typography>
                  </Box>

                  <ContactInformationCard
                    verification={store.currentDetail}
                    supportedPayers={supportedPayers}
                    onPayerMapped={handlePayerMapped}
                    onProductTypeUpdated={handleProductTypeUpdated}
                  />
                </Box>

                <Divider />

                {renderFormSection()}
              </Box>

              <CompletionModal
                open={showCompletionModal}
                onClose={() => setShowCompletionModal(false)}
                onComplete={handleConfirmCompletion}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                selectedOutcome={selectedOutcome}
                setSelectedOutcome={setSelectedOutcome}
                predictedOutcome={
                  getOutcomeAndStatus()?.outcome || "COVERAGE_ACTIVE_HAS_ORTHO"
                }
                formErrors={
                  getCurrentAction() === "form" ? formHook.formErrors : {}
                }
                validationWarnings={
                  getCurrentAction() === "form"
                    ? formHook.validationWarnings
                    : []
                }
                verificationMethod={verificationMethod}
                setVerificationMethod={setVerificationMethod}
                callReferenceNumber={callReferenceNumber}
                setCallReferenceNumber={setCallReferenceNumber}
                hasProof={!!store.currentDetail?.bennyRetrievedFileId}
                onUploadProof={() => fileInputRef.current?.click()}
              />

              <AppliedRulesDialog
                rules={appliedRules}
                open={showRulesDialog}
                onClose={() => setShowRulesDialog(false)}
              />
            </Box>

            {showPDFSidebar && (
              <Box
                sx={{
                  flex: "0 0 45%",
                  position: "sticky",
                  top: 0,
                  height: "100vh",
                  overflow: "hidden",
                  alignSelf: "flex-start",
                }}
              >
                <PDFSidebar
                  fileId={store.currentDetail?.bennyRetrievedFileId ?? null}
                  filePath={store.currentDetail?.bennyRetrievedFilePath ?? null}
                  bennyLastAttemptedAt={
                    store.currentDetail?.bennyLastAttemptedAt ?? null
                  }
                  bennyRetrievalStatus={
                    store.currentDetail?.bennyRetrievalStatus ?? null
                  }
                  bennyRetrievalReason={
                    store.currentDetail?.bennyRetrievalReason ?? null
                  }
                  onUploadPdf={handleUploadPdf}
                  onRemovePdf={handleRemovePdf}
                  headerActions={
                    <EvalCaseActions
                      wieldyId={store.currentDetail.wieldyId}
                      hasFile={Boolean(
                        store.currentDetail.bennyRetrievedFileId,
                      )}
                      queryClient={queryClient}
                      updateSnackBar={updateSnackBar}
                    />
                  }
                />
              </Box>
            )}
          </Box>
        </Box>

        {!showPayerGate && getCurrentAction() && (
          <Box
            sx={{
              px: 3,
              py: 2,
              flexShrink: 0,
              backgroundColor: "background.default",
            }}
          >
            <Box
              sx={{
                p: 3,
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e9ecef",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  System will Auto-Assign:
                </Typography>
                {getOutcomeAndStatus() && (
                  <>
                    <Box
                      sx={{
                        padding: "4px 12px",
                        backgroundColor:
                          getOutcomeAndStatus()?.status === "SKIPPED"
                            ? "#e3f2fd"
                            : "#e8f5e8",
                        borderRadius: "6px",
                        border:
                          getOutcomeAndStatus()?.status === "SKIPPED"
                            ? "1px solid #bbdefb"
                            : "1px solid #c8e6c9",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            getOutcomeAndStatus()?.status === "SKIPPED"
                              ? "#1976d2"
                              : "#2e7d32",
                          fontWeight: 600,
                        }}
                      >
                        {getOutcomeAndStatus()?.status}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      +
                    </Typography>
                    <Box
                      sx={{
                        padding: "4px 12px",
                        backgroundColor: (() => {
                          const outcome = getOutcomeAndStatus()?.outcome;
                          if (
                            outcome === "CARRIER_UNAVAILABLE" ||
                            outcome === "PATIENT_NOT_FOUND" ||
                            outcome === "INSUFFICIENT_DATA" ||
                            outcome === "COVERAGE_INACTIVE" ||
                            outcome === "COVERAGE_ACTIVE_NO_ORTHO"
                          ) {
                            return "#ffebee";
                          }
                          if (getOutcomeAndStatus()?.status === "SKIPPED")
                            return "#e3f2fd";
                          return "#e8f5e8";
                        })(),
                        borderRadius: "6px",
                        border: (() => {
                          const outcome = getOutcomeAndStatus()?.outcome;
                          if (
                            outcome === "CARRIER_UNAVAILABLE" ||
                            outcome === "PATIENT_NOT_FOUND" ||
                            outcome === "INSUFFICIENT_DATA" ||
                            outcome === "COVERAGE_INACTIVE" ||
                            outcome === "COVERAGE_ACTIVE_NO_ORTHO"
                          ) {
                            return "1px solid #ffcdd2";
                          }
                          if (getOutcomeAndStatus()?.status === "SKIPPED")
                            return "1px solid #bbdefb";
                          return "1px solid #c8e6c9";
                        })(),
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: (() => {
                            const outcome = getOutcomeAndStatus()?.outcome;
                            if (
                              outcome === "CARRIER_UNAVAILABLE" ||
                              outcome === "PATIENT_NOT_FOUND" ||
                              outcome === "INSUFFICIENT_DATA" ||
                              outcome === "COVERAGE_INACTIVE" ||
                              outcome === "COVERAGE_ACTIVE_NO_ORTHO"
                            ) {
                              return "#d32f2f";
                            }
                            if (getOutcomeAndStatus()?.status === "SKIPPED")
                              return "#1976d2";
                            return "#2e7d32";
                          })(),
                          fontWeight: 600,
                        }}
                      >
                        {(() => {
                          const outcome = getOutcomeAndStatus()?.outcome;
                          return outcome
                            ? formatVerificationOutcome(outcome)
                            : "";
                        })()}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleCompleteVerification}
                  disabled={isCompleteButtonDisabled()}
                  startIcon={<SaveIcon />}
                  size="large"
                  data-testid="completeVerification"
                >
                  Complete Verification
                </Button>
                <PostButton
                  openUpward
                  isPosting={hasChanges()}
                  onPost={async () => {
                    await postInsuranceVerificationPMSUpdateAndNotify(
                      currentDetail,
                      updateStore,
                      queryClient,
                      updateSnackBar,
                    );
                  }}
                  onManualPost={async () => {
                    try {
                      await queryClient.updatePatientInsuranceVerification({
                        wieldyId: currentDetail.wieldyId,
                        practiceId: currentDetail.practiceId,
                        postedState: PostedState[PostedState.MANUALLY_POSTED],
                        postedDatetime: isoNow(),
                      });
                      updateStore({
                        type: "VERIFICATION_POSTED_STATUS_CHANGED",
                        verificationToUpdate: {
                          ...currentDetail,
                          postedState: PostedState[PostedState.MANUALLY_POSTED],
                        },
                      });
                      updateSnackBar({
                        severity: "success",
                        message:
                          "Insurance verification marked as manually posted",
                      });
                    } catch (_error) {
                      updateSnackBar({
                        severity: "error",
                        message:
                          "Failed to mark insurance verification as manually posted",
                      });
                    }
                  }}
                  postedState={currentDetail.postedState}
                  verificationStatus={currentDetail.verificationStatus}
                  postedLatestErrorMessage={
                    currentDetail.postedLatestErrorMessage
                  }
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </Box>
  );
}
